import { useI18n } from '@/hooks/useI18n';
import { Empty } from '@/components/primitives/Empty';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Skeleton } from '@/components/ui/skeleton';
import type { TemplateSummary } from '@/types/api';
import { TemplateRow } from './TemplateRow';

interface TemplatesListProps {
  templates: TemplateSummary[];
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  onOpen: (hash: string) => void;
  periodDays: number;
}

export function TemplatesList({
  templates,
  isLoading,
  error,
  onRetry,
  onOpen,
  periodDays,
}: TemplatesListProps) {
  const { t } = useI18n();
  if (error) return <ErrorInline error={error} onRetry={onRetry} />;

  if (isLoading) {
    return (
      <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-[116px] w-full rounded-[14px]" />
        ))}
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <Empty
        title={t('Aucun template détecté', 'No templates detected')}
        description={t(
          `Aucun groupe de reels avec une caption identique (minimum 3 reels) n'a été trouvé ${periodDays === 0 ? "sur tout l'historique" : `sur les ${periodDays} derniers jours`}.`,
          `No groups of reels with matching captions (at least 3 reels) were found ${periodDays === 0 ? 'across all history' : `over the last ${periodDays} days`}.`,
        )}
      />
    );
  }

  return (
    <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
      {templates.map((t) => (
        <TemplateRow key={t.templateHash} template={t} onClick={() => onOpen(t.templateHash)} />
      ))}
    </div>
  );
}
